import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Clock, MapPin, Truck, AlertCircle } from "lucide-react";
import { getPublicDelivery, formatMXN } from "@/lib/api";

/**
 * Public customer-facing page. Reached by scanning the store's QR.
 * Shows the delivery zones + fees, then a big "Pedir por WhatsApp" CTA.
 * No login required.
 */
export default function EntregaPublic() {
  const { storeId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    getPublicDelivery(storeId)
      .then((d) => { if (mounted) setData(d); })
      .catch((e) => { if (mounted) setError(e?.response?.data?.detail || "No disponible"); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [storeId]);

  if (loading) return <Shell><p className="text-neutral-500 text-center py-20 text-sm font-mono uppercase tracking-widest">Cargando…</p></Shell>;

  if (error) return (
    <Shell>
      <div className="text-center py-20" data-testid="entrega-error">
        <AlertCircle size={40} className="mx-auto text-[#ff5a00] mb-4" />
        <p className="font-display text-2xl tracking-tight mb-2">{error}</p>
        <p className="text-neutral-500 text-sm">Comunícate directamente con el negocio para más información.</p>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <div data-testid="entrega-public">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#25D366]/15 text-[#25D366] text-[10px] font-mono uppercase tracking-widest mb-4">
            <Truck size={11} /> Envíos a domicilio
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter leading-[0.95] text-neutral-900 mb-2" data-testid="entrega-store-name">
            {data.store_name}
          </h1>
          {data.delivery_schedule && (
            <p className="text-neutral-600 flex items-center gap-1.5 text-sm" data-testid="entrega-schedule">
              <Clock size={13} /> {data.delivery_schedule}
            </p>
          )}
        </motion.div>

        {/* Zonas */}
        <motion.section
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-8"
        >
          <p className="font-mono text-[11px] tracking-[0.3em] text-neutral-500 uppercase mb-3">/ Zonas y costos</p>
          {data.delivery_zones.length === 0 ? (
            <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-5 text-neutral-600 text-sm" data-testid="entrega-no-zones">
              Aún no publicamos zonas. Escríbenos por WhatsApp para confirmar cobertura y costo.
            </div>
          ) : (
            <div className="rounded-2xl border border-neutral-200 overflow-hidden" data-testid="entrega-zones">
              <div className="grid grid-cols-[1fr_120px_120px] px-4 py-2.5 bg-neutral-900 text-white text-[10px] font-mono uppercase tracking-widest">
                <span>Colonia</span><span>Envío</span><span>Mín. pedido</span>
              </div>
              {data.delivery_zones.map((z) => (
                <div key={z.id} className="grid grid-cols-[1fr_120px_120px] items-center px-4 py-3 border-t border-neutral-200" data-testid={`entrega-zone-${z.id}`}>
                  <div className="min-w-0">
                    <p className="font-display text-lg tracking-tight flex items-center gap-1.5"><MapPin size={13} className="text-[#25D366]" /> {z.colonia}</p>
                    {z.notes && <p className="text-neutral-500 text-xs mt-0.5">{z.notes}</p>}
                  </div>
                  <span className="font-display text-xl tracking-tighter text-neutral-900">{z.price > 0 ? formatMXN(z.price) : <span className="text-[#25D366]">GRATIS</span>}</span>
                  <span className="font-mono text-neutral-600 text-sm">{z.min_order > 0 ? formatMXN(z.min_order) : "—"}</span>
                </div>
              ))}
            </div>
          )}
        </motion.section>

        {/* CTA WhatsApp */}
        <motion.a
          href={data.whatsapp_link}
          target="_blank" rel="noopener noreferrer"
          initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, delay: 0.25 }}
          className="mt-10 w-full inline-flex items-center justify-center gap-3 py-5 rounded-2xl bg-[#25D366] hover:bg-[#3ee27d] text-white font-display text-2xl md:text-3xl tracking-tighter shadow-[0_20px_60px_-15px_rgba(37,211,102,0.55)]"
          data-testid="entrega-cta"
        >
          <MessageCircle size={22} strokeWidth={2.5} />
          Pedir por WhatsApp
        </motion.a>

        <p className="mt-4 text-neutral-500 text-xs text-center font-mono uppercase tracking-widest">
          Se abrirá el chat con tu pedido listo · WhatsApp +{data.whatsapp_number}
        </p>

        <footer className="mt-16 pt-6 border-t border-neutral-200 text-center">
          <p className="font-mono text-[10px] text-neutral-400 tracking-widest uppercase">
            Servicio de <span className="text-neutral-900 font-bold">CHALANN</span> · POS por voz
          </p>
        </footer>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <main className="min-h-screen bg-[#faf8f2] text-neutral-900" data-testid="entrega-shell">
      <div className="max-w-2xl mx-auto px-6 md:px-10 py-10 md:py-16">
        {children}
      </div>
    </main>
  );
}
